import { ConfirmPassword } from '@ui/form-items';
import { createAsyncSelectors } from '@lib/selectors';
import { GatewayCard } from '../general';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { securityActions } from '@sonar-software/sonar-security';
import { useParams } from 'react-router-dom';

import { AsyncStateAlert, SanitizedHtml } from '@ui/general';
import { Button, Form, Row } from 'antd';
import { COPY_SPECIFIC, EMAILS, FULL_ROUTES, NOTIFICATIONS_COPY } from '@sonar-software/sonar-ts-constants';
import React, { useCallback, useEffect } from 'react';
import { useAction, useApiResource, useSonarSelector } from '@hooks';

type InvitationViewParams = {
	token: string;
};

interface TokenValidationResult {
	isExpired: boolean;
}

const acceptInvitationSelectors = createAsyncSelectors((state) => state.security.invitations.accept.post);

export const InvitationView: React.FC = () => {
	const { token } = useParams<InvitationViewParams>();

	const isErrored = useSonarSelector(acceptInvitationSelectors.isErrored);
	const isFailed = useSonarSelector(acceptInvitationSelectors.isFailed);
	const isRequested = useSonarSelector(acceptInvitationSelectors.isRequested);
	const isSuccessful = useSonarSelector(acceptInvitationSelectors.isReceived);

	const request = useAction(securityActions.requestPostSecurityInvitationsAccept);
	const reset = useAction(securityActions.resetPostSecurityInvitationsAccept);

	const disabled = isRequested || isSuccessful;

	const { resource, loading } = useApiResource<TokenValidationResult>(
		`v3/security/authentication/invite/status/${token}`
	);

	useEffect(() => {
		if (isSuccessful) {
			window.location.replace(FULL_ROUTES.DASHBOARD);
		}
	}, [ isSuccessful ]);

	const handleOnFinish = useCallback(
		(values: { password: string }) => {
			request({
				password: values.password,
				verificationToken: token ?? ''
			});
		},
		[ request, token ]
	);

	const handleOnValuesChange = useCallback(() => {
		if (isErrored || isFailed) {
			reset();
		}
	}, [ isErrored, isFailed, reset ]);

	if (loading) {
		return <LoadingSpinner spinning />;
	}

	if (resource?.isExpired) {
		return (
			<GatewayCard
				id='Invitation-Expired'
				title={ COPY_SPECIFIC.LINK_EXPIRED }>
				<p>
					<SanitizedHtml
						as='span'
						html={ COPY_SPECIFIC.LINK_EXPIRED_DESCRIPTION(EMAILS.SUPPORT) } />
				</p>
			</GatewayCard>
		);
	}

	return (
		<GatewayCard
			id='Invitation'
			title={ COPY_SPECIFIC.ACCEPT_INVITATION }>
			<Form
				onFinish={ handleOnFinish }
				onValuesChange={ handleOnValuesChange }>
				<AsyncStateAlert
					errored={ NOTIFICATIONS_COPY.ERROR_POST_AUTH_ACTIVATE_INVITED_USER }
					failed={ NOTIFICATIONS_COPY.FAILED_POST_AUTH_ACTIVATE_INVITED_USER }
					stateSelector={ acceptInvitationSelectors.state } />
				<ConfirmPassword
					disabled={ disabled }
					id='invitiation-password'
					size='large' />
				<Row justify='end'>
					<Button
						disabled={ disabled }
						htmlType='submit'
						id='invitation-create-submit-button'
						type='primary'>
						{ COPY_SPECIFIC.CREATE_AROVY_ACCOUNT }
					</Button>
				</Row>
			</Form>
		</GatewayCard>
	);
};
